import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

const AdminSetup = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [available, setAvailable] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/setup/status").then((r) => setAvailable(r.data.setup_available)).catch(() => setAvailable(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/setup", { ...form, role: "parent" });
      // Cookie is set server-side; just refresh the auth context so the UI knows.
      await refreshUser();
      toast.success(r.data.promoted ? "Existing account promoted to admin." : "Admin created.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col">
      <div className="container mx-auto py-6"><Link to="/"><Logo size={44} /></Link></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-mm-navy/10 rounded-2xl p-8 fade-up">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel flex items-center gap-2">
            <ShieldCheck size={14} strokeWidth={2.5} /> Mountain HQ
          </div>
          <h1 className="font-heading text-3xl font-black text-mm-navy mt-2">Claim admin access</h1>
          {available === null && <p className="text-mm-navy/60 mt-4">Checking trailhead...</p>}
          {available === false && (
            <div className="mt-6 p-4 rounded-xl bg-mm-navy/5 text-mm-navy/80 flex gap-3 items-start" data-testid="admin-setup-sealed">
              <Lock size={20} className="text-mm-navy mt-0.5" strokeWidth={2.5} />
              <div>
                <div className="font-heading font-bold">Setup is sealed.</div>
                <p className="text-sm mt-1">An admin already exists for this Mind Map. Sign in with the admin account from the regular login page.</p>
                <Link to="/login" className="btn-trail btn-outline-navy text-sm mt-4 inline-flex">Go to login</Link>
              </div>
            </div>
          )}
          {available === true && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-setup-form">
              <p className="text-sm text-mm-navy/70">No admin exists yet. Create one — this route locks itself after the first use.</p>
              <Field label="Admin name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-setup-name" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-setup-email" />
              <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-setup-password" />
              <button type="submit" disabled={loading} className="btn-trail btn-gold w-full" data-testid="admin-setup-submit">
                {loading ? "Claiming..." : "Claim admin access"}
              </button>
              <p className="text-xs text-mm-navy/50">If this email already belongs to a regular user, that account is promoted to admin.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required
      className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
      data-testid={testid} />
  </div>
);

export default AdminSetup;
